import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { IEarningReportV2 } from '@nxsys/types/models/earningReport/v2';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';

type GetEarningStatementColumnsParams = {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  onExport: (item: IEarningReportV2) => void;
  exportLoadingId?: string | null;
};

export const getEarningStatementColumns = ({
  messages,
  payrollCountry,
  onExport,
  exportLoadingId,
}: GetEarningStatementColumnsParams): ColumnsType<IEarningReportV2> => {
  const currencySymbol = showCurrencyByCountry(payrollCountry);

  return [
    {
      key: 'actions',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      width: 80,
      render: (_, record) => {
        const isLoading = exportLoadingId === record.id;
        return (
          <ul className="flex items-center justify-center gap-x-4">
            <li
              className={isLoading ? 'pointer-events-none text-primary-500' : ''}
              onClick={() => onExport(record)}
            >
              <Tooltip title={isLoading ? 'Loading...' : 'Download'}>
                <span>
                  {isLoading ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
          </ul>
        );
      },
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      dataIndex: 'taxPeriod',
      align: 'right',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'taxYear',
      title: messages['common.taxYear'],
      dataIndex: 'taxYear',
      align: 'right',
      width: 120,
    },
    {
      key: 'companyName',
      title: messages['common.companyName'],
      width: 200,
      render: (_, record) => record.companyRef?.companyName || '-',
    },
    {
      key: 'UTRNo',
      title: messages['common.utrNo'],
      width: 150,
      render: (_, record) => record.subcontractorRef?.setting?.UTRNo || '-',
    },
    {
      key: 'NINumber',
      title: getFieldLabelByCountry(payrollCountry ?? '', 'NINo'),
      width: 150,
      render: (_, record) => record.subcontractorRef?.NINumber || '-',
    },
    {
      key: 'totalEarnings',
      title: messages['common.totalEarnings'],
      dataIndex: 'totalEarnings',
      align: 'right',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
    {
      key: 'totalGross',
      title: messages['common.grossAmount'],
      dataIndex: 'gross',
      align: 'right',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
    {
      key: 'totalCisDeduction',
      title: messages['common.cisTaxDeduction'],
      dataIndex: 'cisDeduction',
      align: 'right',
      width: 170,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
    {
      key: 'totalMaterials',
      title: messages['common.material'],
      dataIndex: 'materials',
      align: 'right',
      width: 130,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
    {
      key: 'totalOtherDeduction',
      title: messages['common.otherDeduction'],
      dataIndex: 'otherDeduction',
      align: 'right',
      width: 160,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
    {
      key: 'totalVat',
      title: messages['common.vatPaid'],
      dataIndex: 'vat',
      align: 'right',
      width: 130,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
    {
      key: 'totalNet',
      title: messages['common.netAmount'],
      dataIndex: 'netPay',
      align: 'right',
      width: 130,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value: number) => (
        <span>{formatMoney(value, currencySymbol)}</span>
      ),
    },
  ];
};
